import { useLayoutEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from 'lucide-react';
import { PaymentHttp } from '@/services';
import { getParsedPrice } from '@/utils/getParsedPrice';
import { Flex, LoadingSpin } from '@/components';
import {
  formatPaymentTransactionDateTime,
  formatPaymentTransactionSubtitle,
  getPaymentTransactionStatusKind,
  type PaymentTransactionStatusKind,
} from './paymentTransactionUtils';

const PAGE_SIZE = 10;

function StatusIcon({ kind }: { kind: PaymentTransactionStatusKind }) {
  if (kind === 'success') {
    return <CheckCircle2 className="size-6 shrink-0" style={{ color: '#147d63' }} aria-hidden />;
  }
  return <XCircle className="size-6 shrink-0 text-red-600" aria-hidden />;
}

function iconWrapClass(kind: PaymentTransactionStatusKind): string {
  const base = 'flex h-10 w-10 shrink-0 items-center justify-center rounded-full';
  return kind === 'success' ? `${base} bg-[#e6f6f5]` : `${base} bg-red-50`;
}

export interface WalletTransactionListMobileProps {
  enabled: boolean;
}

export function WalletTransactionListMobile({ enabled }: WalletTransactionListMobileProps) {
  const { t } = useTranslation(['common', 'payment']);

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isError } = useInfiniteQuery({
    queryKey: ['/payment-transactions/current', 'infinite'],
    queryFn: async ({ pageParam }: { pageParam: number }) =>
      (await PaymentHttp.paymentReport(pageParam, PAGE_SIZE)).payload,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.number + 1),
    enabled,
  });

  const items = useMemo(
    () => data?.pages.flatMap((p) => p.content ?? []) ?? [],
    [data?.pages]
  );

  const sentinelRef = useRef<HTMLDivElement>(null);
  const hasNextRef = useRef(hasNextPage);
  const fetchingNextRef = useRef(isFetchingNextPage);
  hasNextRef.current = hasNextPage;
  fetchingNextRef.current = isFetchingNextPage;

  useLayoutEffect(() => {
    if (!enabled) return;
    const sentinel = sentinelRef.current;
    const root = document.querySelector('.page-container-body');
    if (!(sentinel instanceof HTMLElement)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry.isIntersecting) return;
        if (!hasNextRef.current || fetchingNextRef.current) return;
        void fetchNextPage();
      },
      {
        root: root instanceof HTMLElement ? root : null,
        rootMargin: '0px 0px 240px 0px',
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [enabled, fetchNextPage, items.length]);

  if (!enabled) return null;

  if (isError) {
    return null;
  }

  if (isFetching && !data) {
    return (
      <Flex className="w-full bg-white py-12">
        <LoadingSpin text="" size={28} className="m-auto text-nurture-darker" />
      </Flex>
    );
  }

  if (!isFetching && items.length === 0) {
    return (
      <div className="px-3 py-10 text-center text-base text-neutral-500">
        {t('common:messages.no-items-to-show')}
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 max-w-full overflow-x-hidden bg-white">
      <ul className="m-0 flex w-full min-w-0 max-w-full list-none flex-col gap-0 p-0" dir="rtl">
        {items.map((item, index) => {
          const kind = getPaymentTransactionStatusKind(item);
          const primary = formatPaymentTransactionDateTime(item.startDate);
          const secondary = formatPaymentTransactionSubtitle(item.startDate);
          const amountText = `${getParsedPrice(item.amount)} ${t('common:labels.tomans')}`;
          return (
            <li
              key={`${item.paymentCode}-${item.startDate}-${index}`}
              className="flex min-w-0 max-w-full items-center gap-2.5 border-b border-neutral-100 px-3 py-3.5 last:border-b-0"
            >
              <div className={`${iconWrapClass(kind)} shrink-0`}>
                <StatusIcon kind={kind} />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden text-right">
                <span
                  className="inline-flex min-w-0 max-w-full justify-end overflow-hidden text-ellipsis whitespace-nowrap text-base font-semibold text-neutral-900"
                  dir="ltr"
                >
                  {primary}
                </span>
                <div className="truncate text-sm text-neutral-500">{secondary}</div>
              </div>
              <div className="min-w-0 max-w-[42%] shrink-0 truncate text-end text-sm font-medium tabular-nums text-neutral-900 sm:text-base">
                {amountText}
              </div>
            </li>
          );
        })}
      </ul>
      {isFetchingNextPage && (
        <Flex className="w-full py-4">
          <LoadingSpin text="" size={28} className="m-auto text-nurture-darker" />
        </Flex>
      )}
      <div ref={sentinelRef} className="h-4 w-full shrink-0" aria-hidden />
    </div>
  );
}

export default WalletTransactionListMobile;
